import { useState } from 'react'
import { AsyncButton } from '@shared/ui/AsyncButton'
import { EmptyState } from '@shared/ui/EmptyState'
import { Notice } from '@shared/ui/Notice'
import { useToast } from '@shared/ui/useToast'
import { useTrustedDevices } from '@features/devices/hooks/useTrustedDevices'
import type { UiDevice } from '@features/devices/services/devicesFlow'
import { useTranslation } from '@/i18n'

/** show-more button が出るまでに表示するデバイス行数 (リスト肥大化時の描画負荷対策)。 */
const INITIAL_VISIBLE = 5

/**
 * Trusted devices 一覧 + 個別 revoke セクション (Track E-3、Settings レビュー リスク B Medium)。
 *
 * - mount で自動取得 (`useTrustedDevices`)
 * - 各行: friendly name + IP prefix + last used + (current) tag + 「サインアウト」 button
 * - 自身を revoke: sessionStore.clear() 後、上位 RouteGuard が #login へ navigate
 *
 * 折りたたみは `SettingsPage` 側のセクションヘッダのトグルボタンで行う
 * (本セクション内に重複する「閉じる」ボタンは置かない)。
 */

const formatDate = (iso: string, locale: string): string => {
  try {
    const d = new Date(iso)

    return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(d)
  } catch {
    return iso
  }
}

const DeviceRow = ({
  device,
  locale,
  onRevoke,
}: {
  device: UiDevice
  locale: string
  onRevoke: (id: string, isCurrent: boolean) => Promise<boolean>
}): JSX.Element => {
  const { t } = useTranslation()

  const name = device.parsedUa.display ?? t('settings.security.trusted_devices_unknown_ua')

  return (
    <div className="card settings-device-row" data-testid={`device-row-${device.id}`}>
      <div>
        <div className="settings-item-label">
          {name}
          {device.isCurrent && (
            <span className="tag good settings-device-current-tag">
              {t('settings.security.trusted_devices_current_tag')}
            </span>
          )}
        </div>
        <div className="settings-item-sub">
          {device.ipPrefix !== null && <span>{device.ipPrefix}</span>}
          {device.ipPrefix !== null && <span> · </span>}
          <span>
            {t('settings.security.trusted_devices_last_used').replace(
              '{when}',
              formatDate(device.lastUsedAt, locale),
            )}
          </span>
        </div>
      </div>
      <AsyncButton
        className="btn btn-secondary"
        onClick={() => onRevoke(device.id, device.isCurrent)}
        data-testid={`device-revoke-${device.id}`}
      >
        {t('settings.security.trusted_devices_revoke')}
      </AsyncButton>
    </div>
  )
}

export const TrustedDevicesSection = (): JSX.Element => {
  const { t, language } = useTranslation()
  const { state, refresh, revoke, lastErrorKey } = useTrustedDevices()
  const { show: showToast } = useToast()
  const [expanded, setExpanded] = useState(false)
  const locale = language === 'ja' ? 'ja-JP' : 'en-US'

  // revoke 成功時に Toast を出す。current device (このデバイス) を revoke した場合は
  // そのままログアウト扱い: sessionStore は flow 内で clear 済なので、ここで明示的に
  // #login へ遷移させて即ログアウトする (Shell は sessionStore 変化で再描画しないため
  // RouteGuard 任せにせず明示 navigate する)。
  const handleRevoke = async (id: string, isCurrent: boolean): Promise<boolean> => {
    const ok = await revoke(id, isCurrent)
    if (!ok) return false
    if (isCurrent) {
      window.location.hash = 'login'
    } else {
      showToast({ titleKey: 'settings.security.trusted_devices_revoked_toast', tone: 'success' })
    }

    return ok
  }

  return (
    <div className="card settings-security-item" data-testid="trusted-devices-section">
      <Notice tone="info" ico="info">
        {t('settings.security.trusted_devices_intro')}
      </Notice>

      {lastErrorKey !== null && (
        <Notice tone="danger" ico="alert">
          {t(lastErrorKey)}
        </Notice>
      )}

      {state.kind === 'loading' && (
        <div className="settings-item-sub">{t('settings.security.trusted_devices_loading')}</div>
      )}

      {state.kind === 'error' &&
        (state.messageKey === 'settings.security.trusted_devices_error.unauthorized' ? (
          // セッション切れ: authedFetch が既に sessionStore.clear() 済。
          // 再試行は無意味なのでログイン画面へ誘導する。
          <div className="settings-form-actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                window.location.hash = 'login'
              }}
              data-testid="trusted-devices-relogin"
            >
              {t('settings.security.trusted_devices_relogin')}
            </button>
          </div>
        ) : (
          <div className="settings-form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => void refresh()}>
              {t('settings.security.trusted_devices_retry')}
            </button>
          </div>
        ))}

      {state.kind === 'ready' && state.devices.length === 0 && (
        <EmptyState
          icon="shield"
          title={t('settings.security.trusted_devices_empty_title')}
          sub={t('settings.security.trusted_devices_empty')}
        />
      )}

      {state.kind === 'ready' &&
        (expanded ? state.devices : state.devices.slice(0, INITIAL_VISIBLE)).map(d => (
          <DeviceRow key={d.id} device={d} locale={locale} onRevoke={handleRevoke} />
        ))}

      {state.kind === 'ready' && state.devices.length > INITIAL_VISIBLE && (
        <button
          type="button"
          className="btn btn-secondary settings-device-show-more"
          onClick={() => setExpanded(prev => !prev)}
          data-testid="trusted-devices-show-more"
        >
          {expanded
            ? t('settings.security.trusted_devices_show_less')
            : t('settings.security.trusted_devices_show_more', {
                n: state.devices.length - INITIAL_VISIBLE,
              })}
        </button>
      )}
    </div>
  )
}
